/**
 * DTO des runs (DECISIONS §4.2 — section Runs) et protocole anti-triche (DECISIONS §8).
 *
 * Rappel du protocole : le client **simule** et transmet des **faits** (`spawnId` tués,
 * échappés, PV restants). Le serveur recalcule les éclats, tient le solde de la run et décide
 * de la clôture. Aucune réponse ne contient de montant fourni par le client.
 */
import { z } from 'zod';
import { cartePubliqueSchema } from './content.js';
import { modeSchema, runStateSchema, runStatusSchema, tourPoseeSchema, vagueStatusSchema, } from './models.js';
/** Un ennemi programmé dans une vague, identifié par un `spawnId` unique dans la run. */
export const spawnSchema = z
    .object({
    /** Identifiant unique de l'apparition, ex. `w2-17`. Sert de preuve de fin de vague. */
    spawnId: z.string().min(1),
    ennemiId: z.string().min(1),
    /** Instant d'apparition relatif au début de la vague, en ms. */
    apparitionMs: z.number().int().nonnegative(),
})
    .strict();
/** Composition officielle d'une vague, décidée **par le serveur**. */
export const vaguePlanifieeSchema = z
    .object({
    /**
     * Index de la vague dans la **run** : global en mode illimité (0…7 pour le round 1, 8…15
     * pour le round 2, etc.). Le round se déduit côté client (`floor(index / 8) + 1`), le
     * serveur ne stocke aucun numéro de round (DECISIONS §7.6, A-25).
     */
    index: z.number().int().nonnegative(),
    boss: z.boolean(),
    spawns: z.array(spawnSchema),
    /**
     * Multiplicateur de difficulté appliqué par le client aux **PV et à la vitesse** des
     * ennemis de cette vague (DECISIONS §7.6) : `1` en mode limité, `multiplicateur_difficulte_illimite
     * ^ round` en mode illimité, valeur décidée par le serveur. Absent d'une composition
     * enregistrée avant M3-05 : il vaut alors `1`.
     */
    difficulte: z.number().positive().optional(),
})
    .strict();
/** Réponse de `POST /runs` (`201`). */
export const runCreateResponseSchema = z
    .object({
    runId: z.string().uuid(),
    /** Graine de la run, sérialisée en chaîne (entier 64 bits). */
    seed: z.string(),
    state: runStateSchema,
})
    .strict();
/** Réponse de `POST /runs/:runId/waves/:index/start` (`200`). */
export const waveStartResponseSchema = z.object({ wave: vaguePlanifieeSchema }).strict();
/** Réponse de `POST /runs/:runId/waves/:index/result` (`200`). */
export const waveResultResponseSchema = z
    .object({
    /** Éclats recalculés par le serveur pour cette vague (jamais fournis par le client). */
    eclatsGagnes: z.number().int().nonnegative(),
    /** Nouveau solde de run, source de vérité pour l'affichage. */
    eclatsRun: z.number().int().nonnegative(),
    /**
     * Montant **versé au solde persistant** par cette clôture : `0` partout sauf à la fin
     * d'un round réussi en mode illimité (A-25, §7.6), où c'est le solde de run restant
     * (+ `bonus_fin_niveau`). C'est la seule valeur que l'écran de fin de round doit
     * afficher : elle n'est jamais recalculée côté client.
     */
    versement: z.number().int().nonnegative().optional(),
    /** Vague suivante du niveau, `null` si la vague close était la dernière. */
    waveSuivante: z.object({ index: z.number().int().nonnegative() }).strict().nullable(),
})
    .strict();
/** Une des trois cartes proposées après une vague. */
export const optionCarteSchema = z
    .object({
    cardId: z.string(),
    nameKey: z.string(),
    rarity: z.enum(['commune', 'rare', 'legendaire', 'mythique']),
    /** `null` pour une carte verrouillée : l'effet ne transite pas avant acquisition (F13). */
    effect: z.unknown().nullable(),
})
    .strict();
/** Réponse de `POST /runs/:runId/cards/options` (`200`) — exactement 3 options. */
export const cardOptionsResponseSchema = z
    .object({
    options: z.array(optionCarteSchema).length(3),
    /**
     * Carte **bonus de boss** accordée à cette vague (DECISIONS §7 point 15), `null` s'il n'y
     * en a pas. L'octroi est décidé et persisté par le serveur : le client ne fait que
     * l'afficher, et la carte est déjà débloquée quand ce champ est renseigné.
     */
    bonusCardId: z.string().nullable(),
})
    .strict();
/** Réponse de `POST /runs/:runId/cards/choose` (`200`). */
export const cardChooseResponseSchema = z
    .object({
    card: cartePubliqueSchema,
    collection: z.object({ nouvelleCarte: z.boolean() }).strict(),
    /**
     * Carte bonus de boss de la vague, **effet complet** (elle est désormais possédée), ou
     * `null`. Permet au client d'annoncer la seconde carte sans relire l'état de la run.
     */
    bonusCard: cartePubliqueSchema.nullable(),
})
    .strict();
/** Réponse de `POST /runs/:runId/towers` (`200`). */
export const towerPlaceResponseSchema = z
    .object({
    tower: tourPoseeSchema,
    eclatsRun: z.number().int().nonnegative(),
})
    .strict();
/** Réponse de `POST /runs/:runId/towers/:towerId/upgrade` (`200`). */
export const towerUpgradeResponseSchema = z
    .object({
    tower: tourPoseeSchema,
    eclatsRun: z.number().int().nonnegative(),
})
    .strict();
/** Réponse de `POST /runs/:runId/end` (`200`). */
export const runEndResponseSchema = z
    .object({
    /** Éclats du niveau en cours effectivement validés (0 si la run est perdue). */
    eclatsValides: z.number().int().nonnegative(),
    /** Total versé au solde persistant pendant cette run (niveaux déjà validés inclus). */
    /**
     * Nom imposé par DECISIONS §4.2 (`eclatsCredites`) : conservé tel quel pour ne pas dévier du
     * contrat. Exception de vocabulaire à déclarer dans `scripts/check-vocabulaire.mjs`.
     */
    eclatsCredites: z.number().int().nonnegative(),
    niveauAtteint: z.number().int().nonnegative(),
})
    .strict();
/** État d'une vague dans une run (vue de reprise). */
export const vagueEtatSchema = z
    .object({
    index: z.number().int().nonnegative(),
    status: vagueStatusSchema,
})
    .strict();
/** Réponse de `GET /runs/:runId` (`200`) : état d'autorité complet. */
export const runDetailSchema = z
    .object({
    id: z.string().uuid(),
    mode: modeSchema,
    levelId: z.string(),
    contentVersion: z.string(),
    status: runStatusSchema,
    seed: z.string(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    niveauAtteint: z.number().int().nonnegative(),
    eclatsValides: z.number().int().nonnegative(),
    state: runStateSchema,
    waves: z.array(vagueEtatSchema),
    towers: z.array(tourPoseeSchema),
})
    .strict();
/** Réponse de `GET /runs/:runId` : enveloppe `{ run }` (DECISIONS §4.2). */
export const runResponseSchema = z.object({ run: runDetailSchema }).strict();
//# sourceMappingURL=runs.js.map